import React, { useMemo } from "react";
import { prepareFlowTopPathsData } from "features/analytics/studio/utils/flowDisplayPrep";
import { computeTopPathsFromFlow } from "features/analytics/studio/utils/computeTopPathsFromFlow";

const MAX_TOP_PATHS = 100;
const PATH_KEY_SEP = "\x01";

/** Matches computeTopPathsFromFlow defaults for listing up to MAX_TOP_PATHS graph walks. */
const GRAPH_PATH_OPTS = {
  maxPaths: MAX_TOP_PATHS,
  maxCollect: 4000,
  maxDepth: 80,
  maxTraversalPops: 2_000_000,
};

const ACCENT    = "var(--brand-primary)";
const END_COLOR = "var(--feedback-success)";
const MUTED     = "var(--text-muted)";
const BORDER    = "var(--border-default)";
const BG_BAR    = "var(--surface-sunken)";
/** Middle-step bar fill — distinct from first/last accent. */
const MID_BAR   = "var(--border-default)";

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** Short labels for event names (aligned with computeTopPathsFromFlow). */
function fmtEventLabel(name) {
  if (!name) return "—";
  const s = String(name);
  return s.length > 24 ? `${s.slice(0, 23)}…` : s;
}

function pathKeyFromEvents(events) {
  return (events || []).join(PATH_KEY_SEP);
}

export default function FlowTopPathsView({ flowResult, height = 520 }) {
  const prepared = useMemo(
    () => (flowResult ? prepareFlowTopPathsData(flowResult) : null),
    [flowResult]
  );

  const paths = useMemo(() => {
    if ((prepared?.anchors?.length || 0) < 2) return [];
    /** Everyone who did anchor A in range (conversion SQL anchor1) — survives prepareFlowDisplayData. */
    const funnelEntry =
      prepared.anchor1FunnelCount ??
      prepared.anchors?.[0]?.funnelEntryCount ??
      prepared.anchors?.[0]?.count ??
      0;
    const distinct = prepared.topPathsDistinct;
    if (Array.isArray(distinct) && distinct.length > 0) {
      const seen = new Set();
      const out = [];
      for (let idx = 0; idx < distinct.length && out.length < MAX_TOP_PATHS; idx++) {
        const row = distinct[idx];
        const events = row.events || [];
        const n = row.userCount ?? 0;
        const k = pathKeyFromEvents(events);
        if (seen.has(k)) continue;
        seen.add(k);
        const counts = events.map((_, i) => (i === 0 ? funnelEntry : n));
        out.push({
          id: `distinct-${out.length}`,
          events,
          labels: events.map(fmtEventLabel),
          counts,
          endVolume: n,
        });
      }
      // SQL returns one row per real user-journey sequence; DFS can list more graph walks. Pad to MAX_TOP_PATHS.
      if (out.length < MAX_TOP_PATHS && prepared.nodes?.length) {
        const dfsPaths = computeTopPathsFromFlow(
          prepared.nodes,
          prepared.links,
          prepared.anchors,
          GRAPH_PATH_OPTS
        );
        for (const p of dfsPaths) {
          if (out.length >= MAX_TOP_PATHS) break;
          const k = pathKeyFromEvents(p.events);
          if (seen.has(k)) continue;
          seen.add(k);
          out.push({
            ...p,
            id: `dfs-${out.length}`,
            labels: p.labels || (p.events || []).map(fmtEventLabel),
            counts: (p.events || []).map((_, i) =>
              i === 0 ? funnelEntry : p.counts[i] ?? 0
            ),
            endVolume: p.endVolume,
          });
        }
      }
      return out;
    }
    if (!prepared?.nodes?.length) return [];
    const dfsPaths = computeTopPathsFromFlow(
      prepared.nodes,
      prepared.links,
      prepared.anchors,
      GRAPH_PATH_OPTS
    );
    return dfsPaths.map((p) => ({
      ...p,
      counts: (p.events || []).map((_, i) =>
        i === 0 ? funnelEntry : p.counts[i] ?? 0
      ),
    }));
  }, [prepared]);

  if (!paths.length) {
    return (
      <div
        style={{
          minHeight: height,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          color: MUTED,
          fontSize: 13,
          fontFamily: "Inter, sans-serif",
        }}
      >
        <span style={{ fontWeight: 600, color: "var(--text-secondary)" }}>
          No paths found
        </span>
        <span>
          No path visits every anchor column in order. Try a wider time range, or adjust steps
          before/after so the Sankey links reach each anchor column: not only the first event.
        </span>
      </div>
    );
  }

  const cohortDenominator =
    prepared?.anchor1FunnelCount ??
    prepared?.anchors?.[0]?.funnelEntryCount ??
    prepared?.anchors?.[0]?.count ??
    0;
  const maxCount = Math.max(
    cohortDenominator,
    ...paths.flatMap((p) => p.counts || [p.endVolume || 0]),
    1
  );

  return (
    <div
      style={{
        fontFamily: "Inter, sans-serif",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {paths.map((path, idx) => (
        <PathCard
          key={path.id || idx}
          path={path}
          rank={idx + 1}
          maxCount={maxCount}
          cohortDenominator={cohortDenominator}
        />
      ))}
    </div>
  );
}

function PathCard({ path, rank, maxCount, cohortDenominator = 0 }) {
  const { events, counts, endVolume } = path;
  if (!events?.length || events.length < 2) return null;

  const startCount = counts[0] || 1;
  const denom = cohortDenominator > 0 ? cohortDenominator : startCount;
  const convPct = denom > 0 ? ((endVolume / denom) * 100).toFixed(1) : "0.0";

  return (
    <div
      style={{
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        background: "var(--surface-base)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 14px",
          borderBottom: `1px solid ${BORDER}`,
          fontSize: 12,
        }}
      >
        <span style={{ color: MUTED }}>Path #{rank}</span>
        <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>
          {fmt(endVolume)} users &middot; {convPct}% overall
        </span>
      </div>

      {/* Steps */}
      <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 5 }}>
        {events.map((ev, i) => {
          const count    = counts[i] ?? 0;
          const isFirst  = i === 0;
          const isLast   = i === events.length - 1;
          const isMiddle = !isFirst && !isLast;
          const barPct   = maxCount > 0 ? Math.max(2, (count / maxCount) * 100) : 2;
          const barColor = isLast ? END_COLOR : isFirst ? ACCENT : MID_BAR;
          const pillBg   = isLast ? END_COLOR : isFirst ? ACCENT : BG_BAR;
          const pillBorder = isLast ? END_COLOR : isFirst ? ACCENT : BORDER;

          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Step index pill */}
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: pillBg,
                  border: `1px solid ${pillBorder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  color: isFirst || isLast ? "var(--surface-base)" : MUTED,
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </div>

              {/* Event name */}
              <div
                title={ev}
                style={{
                  width: 180,
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: isFirst || isLast ? 600 : 400,
                  color: isFirst ? ACCENT : isLast ? "var(--text-primary)" : "var(--text-secondary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ev}
              </div>

              {/* Bar */}
              <div
                style={{
                  flex: 1,
                  height: 14,
                  background: BG_BAR,
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${barPct}%`,
                    height: "100%",
                    background: barColor,
                    borderRadius: 6,
                    opacity: isMiddle ? 0.85 : 1,
                  }}
                />
              </div>

              {/* Count */}
              <div
                style={{
                  width: 52,
                  flexShrink: 0,
                  fontSize: 11,
                  fontWeight: isFirst || isLast ? 600 : 400,
                  color: isFirst ? ACCENT : isLast ? "var(--text-primary)" : MUTED,
                  textAlign: "right",
                }}
              >
                {fmt(count)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
